import {useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {ArrowLeft, LockKey} from '@phosphor-icons/react';
import {Field} from '@/components/ui/Field';
import {TenantFooter} from '@/components/layout/TenantFooter';
import {TenantHeader} from '@/components/layout/TenantHeader';
import {isSupabaseConfigured, supabase} from '@/lib/supabase/client';
import {ApiFailure, demoMode} from '@/lib/api/client';
import {ownerPath, tenantPath} from '@/lib/tenant/resolve';

export function OwnerLoginPage() {
  const {slug = ''} = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit() {
    if (!supabase) {
      setError('Supabase не настроен. См. SETUP.md.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const {error: authError} = await supabase.auth.signInWithPassword({email, password});
      if (authError) throw new ApiFailure(authError.message, 'auth', 401);
      void navigate(ownerPath(slug, 'schedule'), {replace: true});
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : 'Не удалось войти');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <TenantHeader />
      <div className="stagger flex flex-col gap-6 px-4 pb-10 pt-6">
        <Link to={tenantPath(slug)} className="flex items-center gap-2 text-[14px] text-muted-foreground">
          <ArrowLeft size={20} aria-hidden />
          На главную
        </Link>
        <span className="flex h-[72px] w-[72px] items-center justify-center rounded-3xl border border-white/10 bg-white/5 text-foreground">
          <LockKey size={32} weight="fill" aria-hidden />
        </span>
        <div className="flex flex-col gap-3">
          <h1 className="m-0 text-[32px] font-bold leading-[1.05] tracking-tight text-foreground">Кабинет студии</h1>
          <p className="m-0 text-[16px] leading-relaxed text-muted-foreground">
            {isSupabaseConfigured
              ? 'Войдите, чтобы управлять записями'
              : demoMode()
                ? 'Это демонстрация без базы данных, кабинет выключен.'
                : 'Supabase не настроен. Инструкция — в SETUP.md.'}
          </p>
        </div>
        {isSupabaseConfigured ? (
          <form
            className="flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Field label="Почта" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
            <Field
              label="Пароль"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            {error ? (
              <p role="alert" className="m-0 text-[15px] text-muted-foreground">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={pending}
              className="btn-accent rounded-full px-6 py-3.5 text-[16px] font-semibold disabled:opacity-60"
            >
              {pending ? 'Входим…' : 'Войти'}
            </button>
          </form>
        ) : null}
      </div>
      <TenantFooter />
    </div>
  );
}
